import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Package, Clock, CheckCircle, ChefHat, Truck, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { apiRequest, formatPrice } from '../lib/utils';

const OrdersPage = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrders = async () => {
      if (!isAuthenticated) { setLoading(false); return; }
      try {
        const data = await apiRequest('/orders');
        setOrders(data);
      } catch (error) {
        console.error('Error fetching orders:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [isAuthenticated]);

  const getStatusIcon = (status) => {
    const icons = { pending: Clock, confirmed: CheckCircle, preparing: ChefHat, ready: Package, delivered: Truck, cancelled: XCircle };
    const Icon = icons[status] || Clock;
    return <Icon className="h-4 w-4" />;
  };

  const getStatusColor = (status) => {
    const colors = { pending: 'bg-yellow-500/20 text-yellow-500', confirmed: 'bg-green-500/20 text-green-500', preparing: 'bg-blue-500/20 text-blue-500', ready: 'bg-purple-500/20 text-purple-500', delivered: 'bg-green-500/20 text-green-500', cancelled: 'bg-red-500/20 text-red-500' };
    return colors[status] || 'bg-zinc-500/20 text-zinc-500';
  };

  const getStatusLabel = (status) => {
    const key = `orders.${status}`;
    return t(key);
  };

  const getPaymentStatusLabel = (status) => {
    return status === 'paid' ? t('orders.paid') : t('orders.pending');
  };

  const getPaymentMethodLabel = (method) => {
    return method === 'card' ? t('orders.card') : t('orders.cod');
  };

  const getDateLocale = () => {
    const locales = { it: 'it-IT', de: 'de-DE', ar: 'ar-SA', en: 'en-US' };
    return locales[i18n.language] || 'it-IT';
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen py-16">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <Package className="h-16 w-16 text-zinc-600 mx-auto mb-4" />
          <h1 className="text-3xl font-bold mb-4">{t('orders.trackOrders')}</h1>
          <p className="text-zinc-400 mb-8">{t('orders.signInToView')}</p>
          <Link to="/login">
            <Button className="bg-[#FFC107] text-black hover:bg-[#F59E0B]" data-testid="login-to-view-orders">
              {t('auth.login')}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen py-8">
        <div className="max-w-4xl mx-auto px-4">
          <h1 className="text-4xl font-bold uppercase tracking-tight text-white mb-8 font-['Barlow_Condensed']">
            {t('orders.myOrders')}
          </h1>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse"><div className="h-32 bg-[#1C1C22] rounded-xl" /></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const titleParts = t('orders.myOrders').split(' ');

  return (
    <div className="min-h-screen py-8" data-testid="orders-page">
      <div className="max-w-4xl mx-auto px-4">
        <h1 className="text-4xl font-bold uppercase tracking-tight text-white mb-8 font-['Barlow_Condensed']">
          {titleParts[0]} <span className="text-[#FFC107]">{titleParts.slice(1).join(' ')}</span>
        </h1>

        {orders.length === 0 ? (
          <div className="text-center py-16">
            <Package className="h-16 w-16 text-zinc-600 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold mb-2">{t('orders.noOrders')}</h2>
            <p className="text-zinc-400 mb-6">{t('orders.noOrdersDesc')}</p>
            <Link to="/menu">
              <Button className="bg-[#FFC107] text-black hover:bg-[#F59E0B]" data-testid="browse-menu-orders">
                {t('orders.browseMenu')}
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <Card key={order.id} className="bg-[#141417] border-[#27272A]" data-testid={`order-${order.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-zinc-400">{t('orders.orderNumber')} #{order.id?.slice(0, 8)}</p>
                      <p className="text-xs text-zinc-500">
                        {new Date(order.created_at).toLocaleDateString(getDateLocale(), {
                          year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <Badge className={`${getStatusColor(order.order_status)} flex items-center gap-1`}>
                      {getStatusIcon(order.order_status)}
                      {getStatusLabel(order.order_status)}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 mb-4">
                    {order.items?.slice(0, 3).map((item, index) => (
                      <div key={index} className="flex justify-between text-sm">
                        <span className="text-zinc-300">{item.quantity}x {item.name}</span>
                        <span className="text-zinc-400">{formatPrice(item.price * item.quantity)}</span>
                      </div>
                    ))}
                    {order.items?.length > 3 && (
                      <p className="text-sm text-zinc-500">{t('orders.moreItems', { count: order.items.length - 3 })}</p>
                    )}
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-[#27272A]">
                    <div className="flex items-center gap-4">
                      <Badge variant="outline" className="border-zinc-700">{getPaymentMethodLabel(order.payment_method)}</Badge>
                      <Badge variant="outline" className={order.payment_status === 'paid' ? 'border-green-500 text-green-500' : 'border-yellow-500 text-yellow-500'}>
                        {getPaymentStatusLabel(order.payment_status)}
                      </Badge>
                    </div>
                    <p className="text-lg font-bold text-[#FFC107]">{formatPrice(order.total)}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrdersPage;
